// Implements: F-UIX-014, F-UIX-015, F-UIX-021, F-VER-004, F-VER-006, F-VER-007
/**
 * `/mockups/:mockupId/versions/:versionId` — one version of a mockup.
 *
 * The page only composes: the version, its promote and its downloads live in
 * `VersionDetailContainer`, and the comparison in `VersionCompareContainer`
 * (F-UIX-009). Both identifiers are still rendered: the address carries them,
 * and this is the page a
 * link opened from inside the application lands on. A reload or a pasted link
 * lands here too: the API lives under `/api` (OD-2), so the edge never sends
 * `/mockups/...` to it. The containers are keyed by version, because the same
 * route element is reused when only the parameter changes and a chosen
 * comparison belongs to ONE version.
 */

import type { ReactElement } from "react";
import { Link, useParams } from "react-router";

import { VersionCompareContainer } from "../../modules/versions/containers/VersionCompareContainer";
import { VersionDetailContainer } from "../../modules/versions/containers/VersionDetailContainer";
import { useTranslation } from "../../shared/i18n";
import styles from "./PlaceholderPage.module.css";

export function VersionPage(): ReactElement {
  const { t } = useTranslation();
  const { mockupId, versionId } = useParams();

  return (
    <section className={styles.page}>
      <h1 className={styles.title}>{t("version.title")}</h1>
      {mockupId === undefined ? null : (
        <Link className={styles.back} to={`/lienzo/${encodeURIComponent(mockupId)}`}>
          {t("version.back")}
        </Link>
      )}
      <p className={styles.identifier} data-testid="mockup-id">
        {mockupId}
      </p>
      <p className={styles.identifier} data-testid="version-id">
        {versionId}
      </p>
      {mockupId === undefined || versionId === undefined ? null : (
        <>
          <VersionDetailContainer
            key={`detail-${versionId}`}
            mockupId={mockupId}
            versionId={versionId}
          />
          <VersionCompareContainer
            key={`compare-${versionId}`}
            mockupId={mockupId}
            versionId={versionId}
          />
        </>
      )}
    </section>
  );
}
